import { useEffect, useRef, useState } from "react";
import { addDays, startOfDay } from "date-fns";
import { addDaysToYmd, timeZoneDateYmd, zonedMidnightToUtcIso } from "@/lib/date-utils";

/**
 * Returns the current date, updating (and re-rendering the caller) once at
 * each local midnight so "today"-scoped query keys/predicates/memos derived
 * from it refresh instead of freezing at whatever moment the caller mounted.
 * The returned Date is stable across renders within the same day, so it's
 * safe to pass into useMemo/useQuery dependency arrays and query keys
 * without causing extra refetches on every render.
 *
 * The scheduling effect has no dependency array — it reruns after every
 * render, always recomputing the wait from the current time, so a tab
 * suspended/throttled past its originally-scheduled timeout still catches up
 * correctly on its next render rather than drifting.
 */
export function useMidnightTick(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const msUntilNextMidnight =
      startOfDay(addDays(new Date(), 1)).getTime() - Date.now() + 1000;
    const timer = setTimeout(() => setNow(new Date()), msUntilNextMidnight);
    return () => clearTimeout(timer);
  });
  return now;
}

/**
 * Like useMidnightTick, but ticks at the organization's own midnight rather
 * than the viewer's browser-local midnight. A staff member viewing a
 * dashboard from a different timezone than the organization would otherwise
 * never get a re-render at the organization's actual day boundary — a
 * `new Date()` read elsewhere in the same render only reflects the org's new
 * day once some OTHER re-render happens to occur, which can lag by up to
 * nearly a full day (Codex round-30 finding on #5782). Pass this hook's
 * return value (not a fresh `new Date()`) into any org-day derivation that
 * must stay reactive to the organization's midnight crossing.
 *
 * A timezone change also refreshes the returned Date. The scheduling effect
 * below only reschedules the next timer, so without this the value stayed at
 * whatever instant the previous organization's midnight (or the mount) took:
 * a staff member switching organizations on an open page read the new
 * organization's day from an hours-old instant, leaving it a day behind until
 * that organization's own next midnight (Codex on #6643).
 */
export function useOrgMidnightTick(orgTimezone: string | null | undefined): Date {
  const [now, setNow] = useState(() => new Date());

  // Not on the first run: the mount's own `new Date()` is already current, and
  // re-setting it would hand every caller a new Date identity for nothing.
  const lastTimezone = useRef(orgTimezone);
  useEffect(() => {
    if (lastTimezone.current === orgTimezone) return;
    lastTimezone.current = orgTimezone;
    setNow(new Date());
  }, [orgTimezone]);

  useEffect(() => {
    const todayStr = timeZoneDateYmd(orgTimezone, new Date());
    const tomorrowStr = addDaysToYmd(todayStr, 1);
    const nextMidnightMs = zonedMidnightToUtcIso(tomorrowStr, orgTimezone)
      ? new Date(zonedMidnightToUtcIso(tomorrowStr, orgTimezone)!).getTime()
      : startOfDay(addDays(new Date(), 1)).getTime();
    const msUntilNextMidnight = nextMidnightMs - Date.now() + 1000;
    const timer = setTimeout(() => setNow(new Date()), msUntilNextMidnight);
    return () => clearTimeout(timer);
  });
  return now;
}
